/**
 * Document Sharing API Endpoints
 *
 * Manage document access control via the generic resource-share surface
 * (api-service-go/resource_share.go is the contract authority):
 *
 *   POST   /documents/{id}/shares
 *   GET    /documents/{id}/shares
 *   DELETE /documents/{id}/shares/{principal_type}/{principal_id}
 *
 * Principals are people (user), role groups (group) or org units (node).
 * In an approval-required environment a group/node share from a non-approver
 * returns 202 APPROVAL_PENDING (see accessRequests.ts) instead of a grant.
 */

import { api } from '../client';
import type { Page } from '../types';
import { createLogger } from '@/utils/logger';
import { isApprovalPending, type ApprovalPending } from './accessRequests';
import { randomUUID } from '@/lib/uuid';

const logger = createLogger('SharingAPI');

export type ShareRole = 'viewer' | 'editor' | 'owner';

/**
 * Grant targets the ACL substrate understands. 'group' is a role group
 * (everyone holding the role), 'node' is an org unit's working set (everyone
 * who works there). Mirrors `validPrincipalTypes` in resource_share.go.
 */
export type SharePrincipalType = 'user' | 'group' | 'node';

// EFFECTIVE_ROLE_UNSPECIFIED is the wire string emitted by api-service-go
// when the ACL service returns Allowed=true with Role_UNSPECIFIED — a
// contract violation surfaced on the wire instead of silently downgraded
// to "viewer". MUST equal `AccessUnspecified` in
// api-service-go/types.go. Renaming either side without updating the
// other is the chip-loss bug fingerprint (PR #1270). Exported as a
// const so the literal lives in exactly one place per language.
export const EFFECTIVE_ROLE_UNSPECIFIED = 'unspecified' as const;

// EffectiveRole is the role the SERVER reports for the current user on
// a resource. Uses ShareRole for the three grant-valid roles, plus the
// EFFECTIVE_ROLE_UNSPECIFIED contract-violation surface.
//
// Use ShareRole for client→server grant inputs (where 'unspecified' is
// not a valid choice). Use EffectiveRole for server→client reads of an
// existing access level.
export type EffectiveRole = ShareRole | typeof EFFECTIVE_ROLE_UNSPECIFIED;

/**
 * Grant target for POST /shares. For type 'user' supply `id` OR `email`
 * (the server resolves either); for 'group'/'node', `id` is required.
 * Mirrors Go ResourcePrincipal.
 */
export interface SharePrincipal {
  type: SharePrincipalType;
  id?: string;
  email?: string;
}

/** POST body for creating/upserting a share. Mirrors Go ShareResourceRequest. */
export interface UpdateShareRequest {
  principal: SharePrincipal;
  access_level: ShareRole;
  /**
   * A self-expiring share, routed to the conditioned grant and evaluated at
   * Check time. The MODEL decides which (kind, relation) pairs accept one — a
   * relation with no conditioned form answers 422 rather than dropping the
   * expiry silently — so this is offered per kind rather than assumed.
   */
  expires_at?: string;
}

/**
 * One grant row, as returned by both POST and GET. `principal_name` is the
 * resolved display name per principal kind (user → profile name/email,
 * group → role name, node → unit name); empty when resolution missed
 * (deleted role, stale grant). Mirrors Go ResourceGrantResponse.
 */
export interface ResourceGrant {
  grant_id: string;
  resource_type: string;
  resource_id: string;
  principal_type: SharePrincipalType;
  principal_id: string;
  principal_email?: string;
  principal_name?: string;
  access_level: ShareRole;
  granted_by: string;
  granted_at: string;
  expires_at?: string;
}

/**
 * GET /documents/{id}/shares — the one list envelope. The grants are read whole
 * (no window), so the response carries no `Link: rel="next"`.
 */
export type SharesResponse = Page<ResourceGrant>;

// ---------------------------------------------------------------------------
// The shareable kinds, and the one client that serves all of them
// ---------------------------------------------------------------------------

/**
 * A resource whose access is managed through the sharing contract.
 *
 * The backend serves every one of these through the SAME handlers
 * (HandleShareResource / HandleListResourceShares / HandleRevokeResourceShare);
 * only the collection segment differs. So the client is one function per verb
 * keyed on the kind, not one module per kind — there used to be three
 * near-identical trios (`listShares` for documents, `listSessionShares`,
 * `listLocationShares`), and a change to the contract had to be made in three
 * places or silently applied to two of them.
 *
 * Adding a kind is an entry in SHAREABLE_COLLECTION, not a new function.
 */
export type ShareableKind = 'document' | 'session' | 'node';

/**
 * The REST collection each kind lives under. `node` is the access graph's word
 * for a unit and is what the grant rows carry, but the route is /locations —
 * this map is the one place that difference is stated.
 */
const SHAREABLE_COLLECTION: Record<ShareableKind, string> = {
  document: 'documents',
  session: 'sessions',
  node: 'locations',
};

function sharesPath(kind: ShareableKind, resourceId: string): string {
  return `/${SHAREABLE_COLLECTION[kind]}/${encodeURIComponent(resourceId)}/shares`;
}

/** GET /{collection}/{id}/shares — every current grant, read whole. */
export async function listResourceShares(
  kind: ShareableKind,
  resourceId: string
): Promise<ResourceGrant[]> {
  try {
    const grants = await api.getAllItems<SharesResponse>(sharesPath(kind, resourceId));
    logger.info('Shares listed', { kind, resourceId, count: grants.length });
    return grants;
  } catch (error) {
    logger.error('Failed to list shares', { kind, resourceId, error });
    throw error;
  }
}

/**
 * POST /{collection}/{id}/shares — create or upsert a share.
 *
 * The ACL Grant is an idempotent upsert, so posting the same principal at a new
 * access_level updates it. Returns the grant row (201), or the APPROVAL_PENDING
 * shape (202) when a group or node share in an approval-required environment
 * was turned into a pending access request.
 *
 * `idempotencyKey` is optional: pass a STABLE one when a multi-step flow must
 * replay the same share on retry (the handover does this). Omitted, a fresh key
 * is minted per invocation, which only dedupes transport-level retries within
 * one call — a user re-click mints a new key. The load-bearing retry safety is
 * SERVER-SIDE; do not remove that thinking this key covers it.
 */
export async function shareResourceOfKind(
  kind: ShareableKind,
  resourceId: string,
  request: UpdateShareRequest,
  idempotencyKey?: string
): Promise<ResourceGrant | ApprovalPending> {
  try {
    const response = await api.post<ResourceGrant | ApprovalPending>(
      sharesPath(kind, resourceId),
      request,
      { headers: { 'Idempotency-Key': idempotencyKey ?? randomUUID() } }
    );
    if (isApprovalPending(response)) {
      logger.info('Share sent for approval', { kind, resourceId, requestId: response.request.id });
    } else {
      logger.info('Share updated', { kind, resourceId, grantId: response.grant_id });
    }
    return response;
  } catch (error) {
    logger.error('Failed to update share', { kind, resourceId, error });
    throw error;
  }
}

/**
 * DELETE /{collection}/{id}/shares/{principal_type}/{principal_id} — remove a
 * principal's access entirely (every role they hold), which is the "revoke this
 * access" intent rather than one tier of it.
 */
export async function revokeResourceShareOfKind(
  kind: ShareableKind,
  resourceId: string,
  principalType: SharePrincipalType,
  principalId: string
): Promise<void> {
  try {
    await api.delete<undefined>(
      `${sharesPath(kind, resourceId)}/${principalType}/${encodeURIComponent(principalId)}`,
      { headers: { 'Idempotency-Key': randomUUID() } }
    );
    logger.info('Share removed', { kind, resourceId, principalType, principalId });
  } catch (error) {
    logger.error('Failed to remove share', { kind, resourceId, principalType, principalId, error });
    throw error;
  }
}



